import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Box, Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';

interface AnalysisPopupProps {
  open: boolean;
  onClose: () => void;
  analysis: string | null;
  isLoading: boolean;
}

export const AnalysisPopup: React.FC<AnalysisPopupProps> = ({ open, onClose, analysis, isLoading }) => {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={commonDialogStyles}>
      <DialogTitle>{t('analysis.title')}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* AI Disclaimer */}
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': {
                  width: '100%',
                }
              }}
            >
              {t('analysis.aiDisclaimer')}
            </Alert>

            {/* Analysis Content */}
            <Box
              sx={{
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  marginTop: 3,
                  marginBottom: 2,
                  color: 'primary.main',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  paddingBottom: 1,
                },
              '& h2': {
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginTop: 3,
                marginBottom: 1.5,
                color: 'primary.dark',
              },
              '& h3': {
                fontSize: '1.25rem',
                fontWeight: '600',
                marginTop: 2,
                marginBottom: 1,
                color: 'text.primary',
              },
              '& p': {
                marginBottom: 1.5,
                lineHeight: 1.7,
                color: 'text.secondary',
              },
              '& ul, & ol': {
                marginLeft: 3,
                marginBottom: 2,
                '& li': {
                  marginBottom: 0.5,
                  lineHeight: 1.6,
                },
              },
              '& strong': {
                fontWeight: 'bold',
                color: 'text.primary',
              },
              '& code': {
                backgroundColor: 'action.hover',
                padding: '2px 6px',
                borderRadius: 1,
                fontSize: '0.9em',
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: 'action.hover',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                marginBottom: 2,
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                paddingLeft: 2,
                marginLeft: 0,
                marginBottom: 2,
                fontStyle: 'italic',
                color: 'text.secondary',
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {analysis || ''}
            </ReactMarkdown>
          </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('analysis.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}; 